import React, { useState } from 'react';
import { getPwaWorkspaceConfig } from '../config/workspace-config';
import { requestPushPermission, setNotificationPromptDismissed } from '../utils/pushManager';
import { isIOS, isStandalone } from '../utils/deviceDetect';

interface NotificationPromptModalProps {
  workspaceId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export const NotificationPromptModal: React.FC<NotificationPromptModalProps> = ({
  workspaceId,
  onClose,
  onSuccess,
}) => {
  const [requesting, setRequesting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const wsConfig = getPwaWorkspaceConfig();
  const appName = wsConfig.appName || 'Console';
  const logoUrl = wsConfig.branding?.logo || '/icons/icon-192.png';

  const isIosBrowser = isIOS() && !isStandalone();

  const handleAllow = async () => {
    setRequesting(true);
    setErrorMessage(null);

    const result = await requestPushPermission(workspaceId);
    setRequesting(false);

    if (result.success) {
      if (onSuccess) onSuccess();
      onClose();
    } else {
      if (result.state === 'denied') {
        setErrorMessage('Notifications are blocked in your browser settings. Please enable permissions in site settings.');
      } else if (result.message) {
        setErrorMessage(result.message);
      } else {
        setErrorMessage("Notifications couldn't be enabled right now. Please try again.");
      }
    }
  };

  const handleDismiss = () => {
    setNotificationPromptDismissed();
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 8, 16, 0.82)',
        backdropFilter: 'blur(6px)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '360px',
          backgroundColor: '#0F172A',
          borderRadius: '24px',
          border: '1px solid rgba(139, 92, 246, 0.25)',
          padding: '28px 24px',
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* App / Notification Icon */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #7C3AED, #4F46E5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '30px',
            marginBottom: '16px',
            boxShadow: '0 8px 20px rgba(124, 58, 237, 0.35)',
            overflow: 'hidden',
          }}
        >
          {logoUrl && logoUrl.startsWith('/') ? (
            <img src={logoUrl} alt={appName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            '🔔'
          )}
        </div>

        <h3
          style={{
            fontSize: '20px',
            fontWeight: '900',
            color: '#FFFFFF',
            marginBottom: '8px',
            letterSpacing: '0.5px',
          }}
        >
          Stay Updated
        </h3>

        <p
          style={{
            fontSize: '13px',
            color: 'rgba(255, 255, 255, 0.65)',
            lineHeight: '1.55',
            marginBottom: '20px',
            maxWidth: '290px',
          }}
        >
          {isIosBrowser
            ? `Install ${appName} to your Home Screen first to enable push notifications and live alerts.`
            : `Get instant updates about your account, new games, promotions, support replies, and activity on ${appName}.`}
        </p>

        {errorMessage && (
          <div
            style={{
              width: '100%',
              backgroundColor: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              padding: '10px 14px',
              fontSize: '12px',
              color: '#F87171',
              lineHeight: '1.4',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            {errorMessage}
          </div>
        )}

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {!isIosBrowser ? (
            <button
              onClick={handleAllow}
              disabled={requesting}
              className="pwa-primary-btn"
              style={{ marginTop: 0, padding: '14px', fontSize: '13px', fontWeight: '800' }}
            >
              {requesting ? 'CONNECTING...' : 'ALLOW NOTIFICATIONS'}
            </button>
          ) : (
            <button
              onClick={() => {
                setNotificationPromptDismissed();
                if (typeof window !== 'undefined') {
                  window.location.href = '/install';
                }
              }}
              className="pwa-primary-btn"
              style={{ marginTop: 0, padding: '14px', fontSize: '13px', fontWeight: '800' }}
            >
              VIEW INSTALL GUIDE
            </button>
          )}

          <button
            onClick={handleDismiss}
            disabled={requesting}
            className="pwa-secondary-btn"
            style={{
              marginTop: 0,
              padding: '12px',
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.5)',
              border: 'none',
              background: 'transparent',
            }}
          >
            Not Now
          </button>
        </div>
      </div>
    </div>
  );
};
